import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Tables } from '@/integrations/supabase/types';
import { INPUT_THEMES, INPUT_STYLE_TOKENS, type InputTheme } from '@/components/ui/form/inputThemes';

type Profession = Tables<'professions'>;

interface ProfessionAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  inputStyle?: React.CSSProperties;
  /** Visual theme. Defaults to 'dark' to keep backward-compatibility. */
  theme?: InputTheme;
}

export function ProfessionAutocomplete({
  value,
  onChange,
  placeholder = 'Search for your profession...',
  className,
  inputStyle,
  theme = 'dark',
}: ProfessionAutocompleteProps) {
  const [professions, setProfessions] = useState<Profession[]>([]);
  const [filteredProfessions, setFilteredProfessions] = useState<Profession[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const tk = INPUT_THEMES[theme];

  useEffect(() => {
    const fetchProfessions = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('professions')
          .select('*')
          .order('name');
        if (error) console.error('Error fetching professions:', error);
        else setProfessions(data || []);
      } catch (error) {
        console.error('Error fetching professions:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProfessions();
  }, []);

  useEffect(() => {
    if (!value) setFilteredProfessions(professions);
    else
      setFilteredProfessions(
        professions.filter(p => p.name.toLowerCase().includes(value.toLowerCase()))
      );
  }, [value, professions]);

  const handleSelect = (profession: Profession) => {
    onChange(profession.name);
    setOpen(false);
  };

  const handleInputChange = (inputValue: string) => {
    onChange(inputValue);
    setOpen(true);
  };

  return (
    <div className="relative">
      <input
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={e => handleInputChange(e.target.value)}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 200)}
        className={[tk.input, 'w-full px-4', className ?? ''].filter(Boolean).join(' ')}
        style={{
          fontSize: INPUT_STYLE_TOKENS[theme].fontSize,
          ...inputStyle,
        }}
      />

      {open && (
        <div
          className={[
            'absolute z-50 w-full mt-1 rounded-md shadow-lg max-h-60 overflow-auto',
            tk.dropdown,
          ].join(' ')}
        >
          {loading ? (
            <div className={['p-4 text-center', tk.helperText].join(' ')}>
              Loading professions...
            </div>
          ) : filteredProfessions.length === 0 ? (
            <div className={['p-4 text-center', tk.helperText].join(' ')}>
              No professions found
            </div>
          ) : (
            <div className="py-1">
              {filteredProfessions.map(profession => (
                <div
                  key={profession.id}
                  className={['flex items-center px-4 py-2', tk.dropdownItem].join(' ')}
                  onClick={() => handleSelect(profession)}
                >
                  <span className={tk.dropdownItemText}>{profession.name}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
